import { useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import { Modal } from '@/components/ui/Modal'
import type { EnvelopeGroupBy } from '@/lib/api/deep-dive.types'
import type { RequestSource } from '@/lib/docusign-data'
import { PanelEmpty, PanelError, PanelLoading } from './PanelState'

/** Slice colors, in legend order. Two greens then two blues, so adjacent
 *  slices stay distinguishable at donut thickness. */
const SLICES = [
  { stroke: 'var(--color-healthy)', dot: 'bg-healthy' },
  { stroke: 'var(--color-primary-bright)', dot: 'bg-primary-bright' },
  { stroke: 'color-mix(in srgb, var(--color-info) 55%, transparent)', dot: 'bg-info/55' },
  { stroke: 'var(--color-info)', dot: 'bg-info' },
]

/** The muted arc and dot everything outside the top four shares. */
const REST = { stroke: 'var(--color-surface-3)', dot: 'bg-fg-subtle' }

const R = 42
const CIRCUMFERENCE = 2 * Math.PI * R

/**
 * How many sources get their own colour and legend cell.
 *
 * Four, so the legend is a 2×2 grid that keeps the panel the same height as the
 * two beside it. The live account reports 24 offices; listing them all was what
 * made this panel three times the height of its row.
 */
const TOP_N = 4

const TABS: { id: EnvelopeGroupBy; label: string }[] = [
  { id: 'office', label: 'Office' },
  { id: 'finance_partner', label: 'Finance Partner' },
]

/** Whole numbers for the big shares, a decimal for the ones that would read 0%. */
function share(count: number, total: number): string {
  const pct = total ? (count / total) * 100 : 0
  return pct >= 1 ? `${Math.round(pct)}%` : `${pct.toFixed(1)}%`
}

/**
 * The palette entry a source is drawn in.
 *
 * The unattributed bucket always takes the muted one, whatever it ranks. It is
 * the absence of an answer rather than an office, and it is currently the
 * largest bucket on both groupings — so a coloured top slice would present "we
 * don't know" as the busiest branch.
 */
function paletteFor(source: { unattributed?: boolean }, rank: number) {
  return source.unattributed ? REST : (SLICES[rank] ?? REST)
}

interface RequestSourceDonutProps {
  sources: RequestSource[]
  groupBy: EnvelopeGroupBy
  onGroupByChange: (next: EnvelopeGroupBy) => void
  isLoading?: boolean
  error?: unknown
  onRetry?: () => void
}

/**
 * Where document requests came from, as a donut with a four-cell legend.
 *
 * Percentages are computed from the counts here rather than taken from the
 * payload's own `pct`, so the legend, the arc and the dialog can never disagree
 * — the three are drawn from one number.
 *
 * Only the top four get a colour; everything else is merged into a single muted
 * arc and reached through "View all". That keeps the donut and the legend
 * describing the same thing: a legend of four against a ring of twenty-four
 * would leave most of the circle unexplained.
 *
 * The tab is not a client-side re-slice: `group_by` is a query parameter, so
 * switching it asks the server for a different grouping of the same period.
 */
export function RequestSourceDonut({
  sources,
  groupBy,
  onGroupByChange,
  isLoading,
  error,
  onRetry,
}: RequestSourceDonutProps) {
  const [showAll, setShowAll] = useState(false)
  /** Arc or legend cell under the cursor, indexed against `arcs`. */
  const [hovered, setHovered] = useState<number | null>(null)

  const { total, ranked, top, restCount } = useMemo(() => {
    const total = sources.reduce((sum, s) => sum + s.count, 0)
    // Sorted here rather than trusting the payload's order, because "top four"
    // has to mean the four largest for the legend and the arcs to agree.
    const ranked = [...sources].sort((a, b) => b.count - a.count)
    const top = ranked.slice(0, TOP_N)
    const restCount = ranked.slice(TOP_N).reduce((sum, s) => sum + s.count, 0)
    return { total, ranked, top, restCount }
  }, [sources])

  const groupLabel = groupBy === 'office' ? 'office' : 'finance partner'
  const arcs = restCount > 0 ? [...top, { code: 'Other', count: restCount }] : top

  // Running start angle, so each arc begins where the previous one ended.
  let offset = 0

  // `h-full` because this panel is wrapped in a col-span div on the DocuSign
  // page, so it is no longer a direct grid child and will not stretch on its own.
  return (
    <section className="border-line bg-surface flex h-full flex-col rounded-lg border p-5">
      <h2 className="text-fg-muted font-mono text-[11px] font-semibold tracking-[0.08em] uppercase">
        Documents Requested By
      </h2>

      <div
        role="tablist"
        aria-label="Group requests by"
        className="border-line bg-input mt-4 flex gap-1 rounded-lg border p-1"
      >
        {TABS.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            type="button"
            aria-selected={tab.id === groupBy}
            onClick={() => onGroupByChange(tab.id)}
            className={cn(
              'flex-1 rounded-md px-3 py-2 text-sm font-semibold transition-colors',
              tab.id === groupBy ? 'bg-info text-white' : 'text-fg-muted hover:text-fg',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <PanelLoading className="flex-1" />
      ) : error ? (
        <PanelError error={error} onRetry={onRetry} className="flex-1" />
      ) : total === 0 ? (
        <PanelEmpty label="No envelopes in this period." className="flex-1" />
      ) : (
        <>
          {/* Chart one side, the offices the other. The names are the tight part
              of this arrangement — they nearly all begin "OWE [", so a cell that
              truncates to "OWE [AZ, Ph…" says nothing — which is why each cell
              stacks its name over its figures instead of sharing a line with
              them, and why the ring is 88px rather than 104px. */}
          <div className="mt-5 flex flex-1 items-center gap-4">
            <div className="relative size-[88px] shrink-0">
              <svg viewBox="0 0 100 100" className="size-full -rotate-90">
                <circle
                  cx="50"
                  cy="50"
                  r={R}
                  fill="none"
                  stroke="var(--color-surface-3)"
                  strokeWidth="11"
                />
                {arcs.map((source, i) => {
                  const length = (source.count / total) * CIRCUMFERENCE
                  const dash = `${length} ${CIRCUMFERENCE - length}`
                  const active = hovered === i
                  const arc = (
                    <circle
                      key={source.code}
                      cx="50"
                      cy="50"
                      r={R}
                      fill="none"
                      stroke={paletteFor(source, i).stroke}
                      // The hovered arc thickens rather than changing colour —
                      // its colour is the only thing tying it to its legend cell.
                      strokeWidth={active ? 14 : 11}
                      strokeDasharray={dash}
                      strokeDashoffset={-offset}
                      opacity={hovered === null || active ? 1 : 0.45}
                      onMouseEnter={() => setHovered(i)}
                      onMouseLeave={() => setHovered(null)}
                      className="cursor-pointer transition-all"
                    />
                  )
                  offset += length
                  return arc
                })}
              </svg>
              {/* The centre carries the total, and the hovered slice's share
                  while one is pointed at — the donut's own label, so the eye
                  does not have to leave the ring to read it. */}
              <span className="pointer-events-none absolute inset-0 grid place-items-center text-center font-mono leading-tight">
                {hovered !== null && arcs[hovered] ? (
                  <span>
                    <span className="text-fg block text-base font-bold">
                      {arcs[hovered].count.toLocaleString()}
                    </span>
                    <span className="text-fg-subtle block text-[10px]">
                      {share(arcs[hovered].count, total)}
                    </span>
                  </span>
                ) : (
                  <span className="text-fg text-lg font-bold">{total.toLocaleString()}</span>
                )}
              </span>
            </div>

            {/* Two columns, four cells — a fixed 2×2 block, so the panel's height
                does not follow the number of offices the account happens to have. */}
            <dl className="grid min-w-0 flex-1 grid-cols-2 gap-x-3 gap-y-3">
              {top.map((source, i) => (
                <div
                  key={source.code}
                  // Even with the whole cell to itself a long name can still
                  // clip ("X-Tac Premier [CA, Riversi…"), so the complete row is
                  // on hover rather than lost to the ellipsis.
                  title={`${source.code} — ${source.count.toLocaleString()} documents · ${share(source.count, total)} of ${total.toLocaleString()}`}
                  onMouseEnter={() => setHovered(i)}
                  onMouseLeave={() => setHovered(null)}
                  className={cn(
                    'min-w-0 cursor-help rounded-md px-1.5 py-1 font-mono text-[11px] transition-colors',
                    hovered === i ? 'bg-surface-3' : 'bg-transparent',
                  )}
                >
                  {/* Wrapped, not truncated. A cell is ~120px and the longest
                      office needs ~152px, so truncating clipped exactly the part
                      that distinguishes one "OWE [..." from the next. Two lines
                      is the cap — enough for every name the account has, and a
                      bound on what one pathological label can do to the row. */}
                  <dt className="text-fg-muted flex min-w-0 items-start gap-1.5">
                    <span
                      className={cn('mt-1 size-2 shrink-0 rounded-full', paletteFor(source, i).dot)}
                    />
                    <span className="line-clamp-2 min-w-0 wrap-break-word">{source.code}</span>
                  </dt>
                  {/* Indented to clear the dot, so the figures line up under the
                      name rather than under its marker. */}
                  <dd className="text-fg mt-0.5 pl-3.5 tabular-nums">
                    {source.count.toLocaleString()}
                    <span className="text-fg-subtle"> · {share(source.count, total)}</span>
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="border-line mt-4 flex items-center justify-between gap-3 border-t pt-3">
            <span className="text-fg-subtle font-mono text-[11px]">
              {ranked.length > TOP_N
                ? `+${ranked.length - TOP_N} more · ${share(restCount, total)}`
                : `${ranked.length} in total`}
            </span>
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="text-info text-[13px] transition-opacity hover:opacity-80"
            >
              View All
            </button>
          </div>
        </>
      )}

      <Modal
        open={showAll}
        onClose={() => setShowAll(false)}
        title={`Documents by ${groupLabel}`}
        subtitle={`${ranked.length.toLocaleString()} ${groupLabel}s · ${total.toLocaleString()} documents`}
        className="max-w-2xl"
      >
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-line border-b">
              <th className="text-fg-subtle py-2 pr-3 text-left font-mono text-[11px] font-semibold tracking-[0.08em] uppercase">
                {groupBy === 'office' ? 'Office' : 'Finance Partner'}
              </th>
              <th className="text-fg-subtle py-2 pr-3 text-right font-mono text-[11px] font-semibold tracking-[0.08em] uppercase">
                Docs
              </th>
              <th className="text-fg-subtle w-40 py-2 text-right font-mono text-[11px] font-semibold tracking-[0.08em] uppercase">
                Share
              </th>
            </tr>
          </thead>
          <tbody>
            {ranked.map((source, i) => (
              <tr
                key={source.code}
                title={`${source.code} — ${source.count.toLocaleString()} documents · ${share(source.count, total)} of ${total.toLocaleString()}`}
                className="border-line hover:bg-surface-2 border-b transition-colors"
              >
                <td className="py-2 pr-3">
                  <span className="flex items-center gap-2">
                    {/* The top four keep the colour they wear on the donut, so a
                        row can be traced back to its arc. */}
                    <span
                      className={cn('size-2 shrink-0 rounded-full', paletteFor(source, i).dot)}
                    />
                    <span className="text-fg font-mono text-[13px]">{source.code}</span>
                  </span>
                </td>
                <td className="text-fg-muted py-2 pr-3 text-right font-mono text-[13px] tabular-nums">
                  {source.count.toLocaleString()}
                </td>
                <td className="py-2">
                  <span className="flex items-center justify-end gap-2">
                    <span className="bg-surface-3 h-1.5 w-24 overflow-hidden rounded-full">
                      <span
                        className={cn('block h-full rounded-full', paletteFor(source, i).dot)}
                        // Scaled against the largest row, not against the total:
                        // at 17% the biggest office would otherwise be a stub and
                        // the rest invisible.
                        style={{ width: `${(source.count / (ranked[0]?.count || 1)) * 100}%` }}
                      />
                    </span>
                    <span className="text-fg-muted w-10 shrink-0 text-right font-mono text-[11px] tabular-nums">
                      {share(source.count, total)}
                    </span>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Modal>
    </section>
  )
}
